import type { LucideIcon } from 'lucide-react';
import { BriefcaseBusiness, Home, Settings, Video, Workflow } from 'lucide-react';

export const MERCHANT_ROUTES = {
  home: '/home',
  productionLines: '/production-lines',
  videos: '/videos',
  businessProfile: '/business-profile/business',
  settings: '/settings',
} as const;

export type MerchantWorkspaceDestination = keyof typeof MERCHANT_ROUTES;

/**
 * The sidebar has two groups, separated by a hairline, exactly as the
 * workspace wireframe draws them: the work itself first, then the
 * utilities that describe the business and the account.
 */
export type MerchantWorkspaceNavigationGroup = 'primary' | 'utility';

export interface MerchantWorkspaceNavigationItem {
  readonly destination: MerchantWorkspaceDestination;
  readonly group: MerchantWorkspaceNavigationGroup;
  readonly path: string;
  readonly icon: LucideIcon;
  readonly label: {
    readonly en: string;
    readonly ar: string;
  };
  readonly matches: (pathname: string) => boolean;
}

export const MERCHANT_WORKSPACE_NAVIGATION: readonly MerchantWorkspaceNavigationItem[] =
  [
    {
      destination: 'home',
      group: 'primary',
      path: MERCHANT_ROUTES.home,
      icon: Home,
      label: { en: 'Home', ar: 'الرئيسية' },
      matches: (pathname) => pathname === MERCHANT_ROUTES.home,
    },
    {
      destination: 'productionLines',
      group: 'primary',
      path: MERCHANT_ROUTES.productionLines,
      icon: Workflow,
      label: { en: 'Production Lines', ar: 'خطوط الإنتاج' },
      matches: (pathname) =>
        pathname === MERCHANT_ROUTES.productionLines ||
        pathname.startsWith('/production-lines/') ||
        pathname.startsWith('/merchant/intakes/'),
    },
    {
      destination: 'videos',
      group: 'primary',
      path: MERCHANT_ROUTES.videos,
      icon: Video,
      label: { en: 'Videos', ar: 'الفيديوهات' },
      matches: (pathname) =>
        pathname === MERCHANT_ROUTES.videos || pathname.startsWith('/videos/'),
    },
    {
      destination: 'businessProfile',
      group: 'utility',
      path: MERCHANT_ROUTES.businessProfile,
      icon: BriefcaseBusiness,
      label: { en: 'Business Profile', ar: 'الملف التجاري' },
      matches: (pathname) =>
        pathname === '/business-profile' || pathname.startsWith('/business-profile/'),
    },
    {
      destination: 'settings',
      group: 'utility',
      path: MERCHANT_ROUTES.settings,
      icon: Settings,
      label: { en: 'Settings', ar: 'الإعدادات' },
      matches: (pathname) =>
        pathname === MERCHANT_ROUTES.settings || pathname.startsWith('/settings/'),
    },
  ] as const;
